import { useState } from "react";
import { Check, ChevronDown, Copy, ExternalLink, Inbox, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useListenedHandled } from "@/hooks/conversation/useListenedHandled";
import {
  buildReplyComposeUrl,
  detectReplyMailApp,
  isListenedEmail,
  REPLY_MAIL_APP_LABELS,
  type ReplyMailApp,
} from "@/lib/conversation/listenedEmail";
import { agentMailboxSelfService } from "@/services/agent/AgentMailboxSelfService";
import { Conversation } from "@/types/conversation/conversation";
import type { ListenedResource } from "@/types/conversation/listened";
import { ListenedAssignee, ListenedHandledBar, ListenedResources, TeachGenieNote } from "./ListenedConversationParts";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const REPLY_APP_STORAGE_KEY = "hg:listenReplyMailApp";

/** A business fact the genie left for the team to fill in, e.g. [confirm refund amount]. */
const REPLY_PLACEHOLDER_RE = /\[[^\]\n]{2,80}\]/;

const readSavedMailApp = (): ReplyMailApp | null => {
  try {
    const value = localStorage.getItem(REPLY_APP_STORAGE_KEY);
    return value && value in REPLY_MAIL_APP_LABELS ? (value as ReplyMailApp) : null;
  } catch {
    return null;
  }
};

const saveMailApp = (app: ReplyMailApp) => {
  try {
    localStorage.setItem(REPLY_APP_STORAGE_KEY, app);
  } catch {
    // Remembering the choice is a convenience only.
  }
};

type Urgency = "low" | "normal" | "high" | "urgent";

interface ListenedEmailAnalysis {
  needs_attention?: boolean;
  urgency?: Urgency;
  category?: string;
  reasoning?: string;
  email?: {
    subject: string | null;
    customer: { email: string | null; name: string | null } | null;
    forwarded_from: string | null;
    mail_url: string | null;
    /** The customer email's own Message-ID; missing on emails analysed before it was passed along. */
    rfc_message_id?: string | null;
    suggested_reply: string | null;
    gap_note: string | null;
    resources: ListenedResource[];
  };
}

const URGENCY_STYLES: Record<Urgency, { label: string; className: string }> = {
  urgent: { label: "Urgent", className: "bg-genie-secondary text-white" },
  high: { label: "High priority", className: "bg-genie-secondary/15 text-genie-secondary" },
  normal: { label: "Needs a reply", className: "bg-genie-accent/15 text-genie-accent-dark" },
  low: { label: "No action needed", className: "bg-gray-100 text-gray-600" },
};

interface ListenedEmailReplyCardProps {
  conversation: Conversation;
}

/**
 * A forwarded email the Listener genie analysed: urgency, the reply it
 * suggests from the genie's knowledge, and matching resources. The team
 * sends it from the genie's forwarding address or from their own inbox
 * (a prefilled compose window), and marks the email handled.
 */
export function ListenedEmailReplyCard({ conversation }: ListenedEmailReplyCardProps) {
  const analysis = (conversation.details?.analysis_result ?? {}) as ListenedEmailAnalysis;
  const email = analysis.email;
  const metadata = (conversation.metadata ?? {}) as Record<string, unknown>;
  const threadId = typeof metadata.mail_thread_id === "string" ? metadata.mail_thread_id : null;

  const [reply, setReply] = useState(email?.suggested_reply ?? "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openedInInbox, setOpenedInInbox] = useState(false);
  const [mailApp, setMailApp] = useState<ReplyMailApp | null>(
    () => detectReplyMailApp(email?.forwarded_from) ?? readSavedMailApp(),
  );
  const { handled, savingHandled, updateHandled, markHandledOnTrust } = useListenedHandled(conversation);

  if (!isListenedEmail(conversation.type) || !email) return null;

  const urgency = URGENCY_STYLES[analysis.urgency ?? "normal"] ?? URGENCY_STYLES.normal;
  const customer = email.customer?.name || email.customer?.email || "the customer";
  const resources = email.resources ?? [];
  const hasPlaceholders = REPLY_PLACEHOLDER_RE.test(reply);

  const openInInbox = async (app: ReplyMailApp) => {
    if (!email.customer?.email) {
      toast.error("No customer address to reply to");
      return;
    }
    saveMailApp(app);
    setMailApp(app);

    window.open(
      buildReplyComposeUrl(app, {
        inbox: email.forwarded_from,
        to: email.customer.email,
        subject: email.subject ?? "",
        body: reply.trim(),
      }),
      "_blank",
      "noopener,noreferrer",
    );
    setOpenedInInbox(true);
    await markHandledOnTrust("No more reminders for this email.");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reply);
      setCopied(true);
      toast.success("Reply copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  const handleSend = async () => {
    if (!conversation.agent_id || !threadId || !reply.trim()) return;
    setSending(true);
    try {
      await agentMailboxSelfService.sendReply(conversation.agent_id, threadId, reply.trim());
      setSent(true);
      toast.success(`Reply sent to ${customer}`);
      if (!handled) await updateHandled(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)" }}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <GenieIcon
                name="mail"
                size={26}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Forwarded email"
              />
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900 truncate">{email.subject || "(no subject)"}</h3>
                <p className="text-xs text-gray-500 truncate">
                  From {customer}
                  {email.forwarded_from ? ` · forwarded by ${email.forwarded_from}` : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${urgency.className}`}>
                {urgency.label}
              </span>
              {analysis.category && (
                <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-600 capitalize">
                  {analysis.category}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-5 space-y-5">
          <ListenedAssignee conversation={conversation} />

          {analysis.reasoning && <p className="text-sm text-gray-600">{analysis.reasoning}</p>}

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-900">
              {email.suggested_reply ? "Suggested reply" : "Your reply"}
            </p>
            {!email.suggested_reply && (
              <p className="text-sm text-gray-600">
                {analysis.needs_attention
                  ? "Your genie couldn't draft a reply this time. Write it here, then send it from your genie's forwarding address or your own inbox."
                  : "Nothing here seems to need a reply. If it does, write it here."}
              </p>
            )}
            {hasPlaceholders && !sent && (
              <p className="text-sm text-gray-700 rounded-lg bg-genie-accent/10 px-3 py-2">
                Fill in the parts in [square brackets] before sending. Your genie didn't know them.
              </p>
            )}
            <textarea
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              placeholder={`Write your reply to ${customer}…`}
              disabled={sent}
              rows={Math.min(14, Math.max(5, reply.split("\n").length + 1))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base sm:text-sm text-gray-800 leading-relaxed outline-none focus:border-genie-primary disabled:bg-gray-50"
            />
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <Button
                type="button"
                onClick={handleSend}
                disabled={sending || sent || !reply.trim() || !threadId}
                className="min-h-[44px] sm:min-h-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : sent ? (
                  <Check className="w-4 h-4 mr-1.5" />
                ) : (
                  <Send className="w-4 h-4 mr-1.5" />
                )}
                {sent ? "Sent" : `Send to ${customer}`}
              </Button>

              {/* With a known mail app the main button opens it straight away and the
                  arrow switches app; otherwise the whole button asks which app first. */}
              <div className="flex">
                {mailApp && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openInInbox(mailApp)}
                    className="min-h-[44px] sm:min-h-0 flex-1 rounded-r-none"
                  >
                    <Inbox className="w-4 h-4 mr-1.5" />
                    Reply from my inbox
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    {mailApp ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="min-h-[44px] sm:min-h-0 rounded-l-none border-l-0 px-2"
                        aria-label="Choose your mail app"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button type="button" variant="outline" className="min-h-[44px] sm:min-h-0 flex-1">
                        <Inbox className="w-4 h-4 mr-1.5" />
                        Reply from my inbox
                        <ChevronDown className="w-4 h-4 ml-1.5" />
                      </Button>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(Object.keys(REPLY_MAIL_APP_LABELS) as ReplyMailApp[]).map((app) => (
                      <DropdownMenuItem key={app} onClick={() => openInInbox(app)}>
                        {app === mailApp && <Check className="w-3.5 h-3.5 mr-1.5" />}
                        {REPLY_MAIL_APP_LABELS[app]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                disabled={!reply.trim()}
                className="min-h-[44px] sm:min-h-0"
              >
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                Copy
              </Button>
              {email.mail_url && (
                <Button type="button" variant="outline" asChild className="min-h-[44px] sm:min-h-0">
                  <a href={email.mail_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-1.5" />
                    Open in Mail
                  </a>
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-400">
              Send goes from your genie's forwarding address as a reply in the customer's thread
              {email.forwarded_from ? (
                <>
                  , with a copy to <span className="font-medium">{email.forwarded_from}</span>
                </>
              ) : null}
              . Reply from my inbox opens a new email in your own mail app with the reply filled in.
            </p>
          </div>

          <ListenedHandledBar
            handled={handled}
            saving={savingHandled}
            onToggle={() => updateHandled(!handled)}
            nudged={openedInInbox}
            handledMessage="Handled. No more reminders for this email."
            openMessage={
              openedInInbox
                ? "Sent it from your inbox? Mark it handled so your genie stops reminding you."
                : "Answered it somewhere else? Mark it handled."
            }
          />

          {email.gap_note && <TeachGenieNote gapNote={email.gap_note} agentId={conversation.agent_id} subject="emails" />}

          <ListenedResources resources={resources} />
        </div>
      </div>
    </div>
  );
}
